/**
 * FP3-033 — Provider Schedule (Print).
 *
 * Day or week view. Uses `GET /api/schedule?provider_id=…&start_date=…&end_date=…`
 * which already returns materialized sessions hydrated with student
 * mini-records.
 *
 * Landscape — letter landscape default for the week view.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  PrintShell,
  PrintAccessGate,
  classifyError,
} from "@/components/print/PrintShell";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function todayIso() { return new Date().toISOString().slice(0, 10); }
function startOfWeek(iso) {
  const d = new Date(iso);
  const dow = d.getDay();
  d.setDate(d.getDate() - dow);
  return d.toISOString().slice(0, 10);
}
function addDays(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function PrintProviderSchedule() {
  const { providerId } = useParams();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "day" ? "day" : "week";
  const date = searchParams.get("date") || todayIso();
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [provider, setProvider] = useState(null);
  const [sessions, setSessions] = useState([]);

  const { startDate, endDate } = useMemo(() => {
    if (view === "day") return { startDate: date, endDate: date };
    const start = startOfWeek(date);
    return { startDate: start, endDate: addDays(start, 6) };
  }, [view, date]);

  // Apply landscape body class for proper print page orientation on
  // week view. Cleanup on unmount.
  useEffect(() => {
    if (view === "week") {
      document.body.classList.add("fp-landscape");
      return () => document.body.classList.remove("fp-landscape");
    }
    return undefined;
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    Promise.all([
      api.getSchedule({ provider_id: providerId, start_date: startDate, end_date: endDate }),
      // Get the current provider as a header hint. If the user is
      // an admin browsing another provider's schedule, this will
      // be the admin's own provider record — the header will show
      // "Provider Schedule" fallback, which is acceptable for MVP.
      api.getCurrentProvider().catch(() => null),
    ])
      .then(([sched, prov]) => {
        if (cancelled) return;
        setSessions(sched.data || []);
        // Only treat as "this provider" if the current-provider id
        // matches the route's providerId. Otherwise leave the
        // header generic — accurate.
        if (prov?.data && prov.data.id === providerId) {
          setProvider(prov.data);
        }
        setPhase("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message);
        setPhase(classifyError(err));
      });
    return () => { cancelled = true; };
  }, [providerId, startDate, endDate]);

  // Group sessions by date for the week table.
  const grouped = useMemo(() => {
    const out = {};
    for (const s of sessions) {
      const d = s.date || "—";
      out[d] = out[d] || [];
      out[d].push(s);
    }
    Object.values(out).forEach((rows) =>
      rows.sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")))
    );
    return out;
  }, [sessions]);

  const days = useMemo(() => {
    if (view === "day") return [startDate];
    const out = [];
    for (let i = 0; i < 7; i++) out.push(addDays(startDate, i));
    return out;
  }, [view, startDate]);

  const providerName = provider
    ? `${provider.first_name || ""} ${provider.last_name || ""}`.trim()
    : "Provider Schedule";

  return (
    <PrintShell
      title="Provider Schedule"
      subtitle={`${providerName} · ${view === "day" ? startDate : `Week of ${startDate}`}`}
      testId="print-provider-schedule"
      landscape={view === "week"}
    >
      <PrintAccessGate phase={phase} error={error}>
        <div className="fp-print-section">
          <div className="fp-print-grid-3">
            <div>
              <div className="fp-print-section-label">Provider</div>
              <div style={{ fontWeight: 700 }}>{providerName}</div>
            </div>
            <div>
              <div className="fp-print-section-label">View</div>
              <div style={{ fontWeight: 700, textTransform: "capitalize" }}>{view}</div>
            </div>
            <div>
              <div className="fp-print-section-label">Period</div>
              <div style={{ fontWeight: 700 }}>{startDate} – {endDate}</div>
            </div>
          </div>
        </div>

        {days.map((dIso) => {
          const rows = grouped[dIso] || [];
          const d = new Date(dIso);
          const label = `${DAYS[d.getDay()]} · ${dIso}`;
          return (
            <div
              key={dIso}
              className="fp-print-section"
              data-testid={`schedule-day-${dIso}`}
            >
              <div className="fp-print-section-title">{label}</div>
              {rows.length === 0 ? (
                <div style={{ fontSize: 11.5, color: "var(--fp-print-soft)" }}>
                  No sessions.
                </div>
              ) : (
                <table className="fp-print-table">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Time</th>
                      <th style={{ width: 110 }}>Service</th>
                      <th>Students</th>
                      <th style={{ width: 90 }}>Location</th>
                      <th style={{ width: 70 }}>Minutes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const time =
                        s.start_time && s.end_time
                          ? `${s.start_time}–${s.end_time}`
                          : s.start_time || "—";
                      const students = (s.students || [])
                        .map((st) => `${st.first_name || ""} ${st.last_name || ""}`.trim())
                        .filter(Boolean);
                      // Personal blocks have no students; surface label.
                      const label = students.length > 0
                        ? students.join(", ")
                        : (s.label || s.title || "Personal / unavailable");
                      return (
                        <tr key={`${s.id || s._key || ""}-${s.date}-${s.start_time}`}>
                          <td>{time}</td>
                          <td>{s.service_area || s.kind || "—"}</td>
                          <td>{label}</td>
                          <td>{s.room?.name || s.location || "—"}</td>
                          <td>{s.minutes_actual ?? s.minutes_scheduled ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </PrintAccessGate>
    </PrintShell>
  );
}
